import React, { useState, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router'

import {
    UiApplicationIcon,
    UiDashboardIcon,
    UiHomeIcon,
    UiLeadsIcon,
    UiLoanStatusIcon,
    UiUiMegaphoneIcon,
    UiResourcesIcon,
    UiPerformanceDashboardIcon,
} from '@octane/spark'
import getLeadsCount from 'api/leads/getLeadsCount'
import { BoatIcon, RVIcon } from 'components/CrossoverNewAppIcons'
import CustomerAgent from 'components/CustomerAgent'
import { DropdownCustomItemProps } from 'components/Navbar/NavbarDropdown/DropdownCustomItem/DropdownCustomItem'
import { DropdownUrlItemProps } from 'components/Navbar/NavbarDropdown/DropdownUrlItem/DropdownUrlItem'
import { NavbarDropdownProps } from 'components/Navbar/NavbarDropdown/NavbarDropdown'
import { UrlMatchType } from 'components/Navbar/NavbarItem'
import { NavbarItemProps } from 'components/Navbar/NavbarItem/NavbarItem'
import TermsOfUseModal from 'components/TermsOfUseModal/TermsOfUseModal'
import useShouldHideForRedirect from 'containers/DealerRedirector/useShouldHideForRedirect'
import { useFeatureFlagContext } from 'contexts/FeatureFlagContextProvider'
import useDashboard from 'hooks/useDashboard'
import useUser from 'hooks/useUser'
import { OctaneLogger } from 'integrations/datadog/OctaneLogger'
import { LEADS_PAGE_ROUTE_PATH } from 'pages/LeadsPage'
import { getAlreadyAddedLenders } from 'pages/MyAccount/Lenders/utils'
import AssetType from 'state/enums/AssetType'
import getNewApplicationPath, {
    LEGACY_APPLICATION_PATH,
    NEW_MARINE_APPLICATION,
} from 'utils/getNewApplicationPath'

import { shouldShowMenus } from './utils'

const shouldShowSelectLenderPage = (userId: number) => {
    const lenders = getAlreadyAddedLenders(userId)

    return lenders.length > 1
}

export type NavbarContainerProps = {
    item: React.ComponentType<NavbarItemProps>
    dropdown: React.ComponentType<NavbarDropdownProps> & {
        UrlItem: React.ComponentType<DropdownUrlItemProps>
        CustomItem: React.ComponentType<DropdownCustomItemProps>
    }
    showCustomerAgent?: boolean
    showPerformance?: boolean
    showLeads?: boolean
    showHelp?: boolean
    showFeedback?: boolean
}
export default function NavbarContainer(
    this: unknown,
    {
        item,
        dropdown,
        showCustomerAgent = true,
        showPerformance = true,
        showLeads = true,
        showHelp = true,
        showFeedback = true,
    }: NavbarContainerProps,
): JSX.Element | null {
    const NavbarItem = item
    const NavbarDropdown = dropdown
    const [isOpen, setIsOpen] = useState(false)

    const {
        user,
        isLoggedIn,
        isAutoDealership,
        assetTypes,
        isMultiAssetDealership,
    } = useUser()

    const shouldHideForRedirect = useShouldHideForRedirect()
    const { hasDashboard } = useDashboard()
    const { pathname } = useLocation()
    const featureFlags = useFeatureFlagContext()

    const [newLeadsCount, setNewLeadsCount] = useState<number | string | null>(
        null,
    )

    const [showNotification, setShowNotification] = useState(true)
    const navigate = useNavigate()

    const handleLeadsPageClick = () => {
        navigate(LEADS_PAGE_ROUTE_PATH)
        setShowNotification(false)
    }

    const isPowersportsDealership =
        user?.dealership?.primaryAssetType === AssetType.POWERSPORTS_VEHICLE

    const isRVDealership =
        user?.dealership?.primaryAssetType === AssetType.RECREATIONAL_VEHICLE

    const dealershipHasMarineAssetType =
        assetTypes.includes(AssetType.MARINE) ?? false

    const isRVMarineCrossoverDealership =
        isRVDealership && dealershipHasMarineAssetType

    let newApplicationPath = getNewApplicationPath(
        featureFlags,
        user?.dealership?.primaryAssetType,
    )

    if (user) {
        if (shouldShowSelectLenderPage(user.id)) {
            newApplicationPath = '#/new-application'
        }
    }

    useEffect(() => {
        let isMounted = true

        async function fetchNewLeadsCount() {
            if (isLoggedIn && showLeads && featureFlags?.leadsPage) {
                try {
                    const leadsCount = await getLeadsCount()
                    const newLeads = leadsCount?.new_leads_count

                    if (isMounted) {
                        if (newLeads > 99) {
                            setNewLeadsCount('99+')
                        } else if (newLeads > 0) {
                            setNewLeadsCount(newLeads)
                        } else {
                            setNewLeadsCount(null)
                        }
                    }
                } catch (error) {
                    if (isMounted) {
                        OctaneLogger.error(
                            `Error fetching total leads count:`,
                            undefined,
                            error as Error,
                        )
                    }
                }
            }
        }

        fetchNewLeadsCount()

        return () => {
            isMounted = false
        }
    }, [
        isLoggedIn,
        showLeads,
        featureFlags?.leadsPage,
        isPowersportsDealership,
    ])

    if (isLoggedIn === undefined) {
        return null
    }

    if (shouldHideForRedirect) {
        return null
    }

    return (
        <>
            <TermsOfUseModal
                isOpen={isOpen}
                handleClose={() => setIsOpen(false)}
            />
            {shouldShowMenus(pathname) && isLoggedIn ? (
                <>
                    {(!isAutoDealership || isMultiAssetDealership) && (
                        <NavbarItem
                            id="menu-home"
                            href="#/"
                            icon={UiHomeIcon}
                            text="Home"
                            urlMatchType={UrlMatchType.Exact}
                        />
                    )}
                    {(hasDashboard || isAutoDealership) &&
                        !isMultiAssetDealership && (
                            <NavbarItem
                                id="menu-dashboard"
                                href="#/dashboard"
                                icon={UiDashboardIcon}
                                text="Dashboard"
                                urlMatchType={UrlMatchType.Exact}
                            />
                        )}
                    {showLeads && featureFlags?.leadsPage && (
                        <NavbarItem
                            id="menu-leads"
                            href={`#/${LEADS_PAGE_ROUTE_PATH}`}
                            icon={UiLeadsIcon}
                            text="Leads"
                            urlMatchType={UrlMatchType.Exact}
                            notificationCount={newLeadsCount}
                            shouldShowNotification={showNotification}
                            onClick={handleLeadsPageClick}
                        />
                    )}
                    {isRVMarineCrossoverDealership &&
                    !featureFlags?.showRVCrossoverExperience ? (
                        <>
                            <NavbarItem
                                id="menu-new-rv-app"
                                href={LEGACY_APPLICATION_PATH}
                                icon={RVIcon}
                                text="New RV App"
                                urlMatchType={UrlMatchType.StartsWith}
                            />
                            <NavbarItem
                                id="menu-new-marine-app"
                                href={NEW_MARINE_APPLICATION}
                                icon={BoatIcon}
                                text="New Marine App"
                                urlMatchType={UrlMatchType.StartsWith}
                                wider
                            />
                        </>
                    ) : (
                        <NavbarItem
                            id="menu-new-app"
                            href={newApplicationPath}
                            icon={UiApplicationIcon}
                            text="New App"
                            urlMatchType={UrlMatchType.StartsWith}
                        />
                    )}

                    <NavbarItem
                        id="menu-status"
                        href="#/saved-applications"
                        icon={UiLoanStatusIcon}
                        text="Status"
                        urlMatchType={UrlMatchType.Exact}
                    />

                    {showPerformance !== false &&
                        user?.dealership?.isPerformanceMetricEnabled && (
                            <NavbarItem
                                id="menu-performance-dashboard"
                                href="#/performance"
                                icon={UiPerformanceDashboardIcon}
                                text="Performance"
                                urlMatchType={UrlMatchType.Exact}
                            />
                        )}

                    {showHelp && (
                        <NavbarDropdown
                            id="menu-help"
                            icon={UiResourcesIcon}
                            text="Help"
                        >
                            <NavbarDropdown.UrlItem
                                text="Request 10-day Payoff"
                                hashNavigate="ten-day-payoff"
                            />
                            <NavbarDropdown.UrlItem
                                text="Contact Us"
                                url={
                                    isAutoDealership
                                        ? 'https://www.roadrunnerfinancial.com/auto-dealer-contact'
                                        : 'https://www.roadrunnerfinancial.com/dealer-contact'
                                }
                            />
                            <NavbarDropdown.CustomItem
                                text="Terms of Use"
                                handleClick={() => setIsOpen(true)}
                            />
                        </NavbarDropdown>
                    )}
                    {showFeedback && (
                        <NavbarItem
                            id="menu-feedback"
                            href="https://roadrunnerfinancial2ndorg.my.site.com/roadrunner/s/dealer-advocacy-feedback"
                            icon={UiUiMegaphoneIcon}
                            text="Feedback"
                            urlMatchType={UrlMatchType.Exact}
                            newTab
                        />
                    )}
                    {showCustomerAgent && (
                        <li>
                            <CustomerAgent
                                agentName={user?.customerAgent?.agentName}
                                phoneNumber={
                                    user?.customerAgent?.agentPhoneNumber
                                }
                                extension={user?.customerAgent?.agentExtension}
                            />
                        </li>
                    )}
                </>
            ) : null}
        </>
    )
}
